<script setup lang="ts">
import { onMounted, ref, onBeforeUnmount } from "vue";
import XIcon from "../icons/XIcon.vue";

const bodyOverflowBackup = ref("");
const modalBodyElement = ref(null);
const closeFromHistoryBack = ref(false);
const parentRouteName = ref("");
const root = ref<HTMLElement>();

const emit = defineEmits(["mounted", "close"]);

onMounted(() => {
  bodyOverflowBackup.value = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  window.addEventListener("popstate", windowPopState);
  window.addEventListener("resize", windowResize);
  document.addEventListener("keydown", documentKeyDown);

  emit("mounted");
});

onBeforeUnmount(() => {
  if (bodyOverflowBackup.value !== "hidden") {
    document.body.style.overflow = bodyOverflowBackup.value;
  }

  window.removeEventListener("popstate", windowPopState);
  window.removeEventListener("resize", windowResize);
  document.removeEventListener("keydown", documentKeyDown);
});

const windowPopState = () => {
  closeFromHistoryBack.value = true;
  emit("close");
};

const windowResize = () => {
  if (root.value && root.value.offsetHeight === 0) {
    emit("close");
  }
};

const documentKeyDown = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    emit("close");
  }
};
</script>

<template>
  <div ref="root" class="app-modal-wrapper">
    <div class="app-modal-container">
      <div class="app-modal-header">
        <h3>
          <slot name="header" />
        </h3>
        <button class="btn-close" @click="emit('close')">
          <XIcon width="27" height="27" />
        </button>
      </div>

      <div ref="modalBodyElement" class="app-modal-body">
        <slot name="body" />
      </div>

      <div class="app-modal-footer">
        <slot name="footer">
          <button class="btn btn-dark" @click="emit('close')">OK</button>
        </slot>
      </div>
    </div>
    <a class="app-modal-backdrop" tabindex="-1" @click="emit('close')" />
  </div>
</template>

<style scoped>
.app-modal-wrapper {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
}

.app-modal-container {
  position: relative;
  z-index: 1;
  overflow: hidden;

  display: flex;
  flex-direction: column;

  width: calc(100% - 20px);
  margin: 20px 10px;
  max-width: 580px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  font-family: Helvetica, Arial, sans-serif;
}

@media screen and (orientation: landscape) {
  .app-modal-container {
    width: calc(100% - 20px);
    margin: 10px 10px;
  }
}

.app-modal-header {
  display: flex;
  align-items: center;
  padding: 0 1rem;
  height: 3.5714285rem;
  position: relative;
  background: var(--dark-color);
  border-bottom: 1px solid var(--primary-color);
  box-sizing: content-box;
  flex-shrink: 0;
}

.app-modal-header h3 {
  margin: 0;
  flex-grow: 1;
  max-height: 3rem;
  overflow: hidden;
  font-size: 1.17rem;
  line-height: 1.25;
  font-weight: bold;
  color: var(--white-ice);

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.app-modal-header .btn-close {
  display: flex;
  background: var(--black-eigengrau);
  border: none;
  font-size: 27px;
  cursor: pointer;
  line-height: 1;
  width: 27px;
  height: 27px;
  color: var(--white-ice);
  margin: 0 0 0 1rem;
  box-sizing: content-box;
  outline: none;
  padding: 3px;
  border-radius: 0.25rem;
  opacity: 1;
}

.app-modal-header .btn-close:not(:disabled):active {
  color: #beaade;
}

.app-modal-header .btn-close:not(:active):focus {
  color: #6e4e9e;
}

.app-modal-header .btn-close:not(:active):focus {
  box-shadow: 0 0 0 3px rgba(81, 65, 157, 0.25);
}

.app-modal-body {
  overflow: auto;
  background: var(--black-eigengrau);
  padding: 1rem;
  color: var(--white-ice);
}

.app-modal-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 1rem;
  min-height: 3.5714285rem;
  position: relative;
  background: var(--dark-color);
  box-sizing: content-box;
  border-top: 1px solid var(--primary-color);
}

.app-modal-footer > div {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding: 0 0 5px 0;
}

.app-modal-footer .btn {
  background-color: var(--black-eigengrau);
  color: var(--white-ice);
}

.app-modal-enter {
  opacity: 0;
}

.app-modal-leave-active {
  opacity: 0;
}

.app-modal-enter .app-modal-container,
.app-modal-leave-active .app-modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.app-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 0;
  width: 100vw;
  height: 100vh;
  /*background-color: #000;*/
  background-color: rgba(18, 15, 33, 0.7);
  /*opacity: .5;*/
}
</style>
